// src/pages/ProjectDetailPage.js
import React, { useState, useMemo, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link, Navigate } from 'react-router-dom';
import LightboxGallery from '../components/common/LightboxGallery';
import LiveSiteViewer from '../components/realisations/LiveSiteViewer';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, A11y, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';

import './ProjectDetailPage.css';
import '../components/home/PortfolioHighlightSection.css'; // Réutilisation des styles
import pb from '../lib/pocketbase';

function pbFileUrl(record, fileField, thumb) {
  try {
    if (!record || !record[fileField]) return '';
    return pb.files.getUrl(record, record[fileField], thumb ? { thumb } : undefined);
  } catch {
    return '';
  }
}

function ProjectDetailPage() {
  const { projectSlug } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [similarProjects, setSimilarProjects] = useState([]); // Nouveau state pour les projets similaires
  const siteUrl = "https://infocolab.com";

  // Charger le projet principal
  useEffect(() => {
    let mounted = true;
    const fetchOne = async () => {
      setLoading(true);
      try {
        const decodedSlug = decodeURIComponent(projectSlug || '').trim();
        console.log("DEBUG InfoColab: Start fetching", { projectSlug, decodedSlug });

        // Fonction simple de slugification pour le fallback
        const slugify = (str) => {
          return String(str)
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '')
            .replace(/-{2,}/g, '-');
        };

        const filter1 = `slug = "${String(decodedSlug).replace(/"/g, '\\"')}" && is_published = true`;
        console.log("DEBUG InfoColab: Filter 1", filter1);

        // 1. Essai correspondance exacte
        let res = await pb.collection('site_realisations').getList(1, 1, {
          filter: filter1,
          expand: 'category_id',
        });

        let it = res?.items?.[0] || null;
        console.log("DEBUG InfoColab: Result 1", it);

        // 2. Si pas trouvé, essai avec le slug "slugifié" (ex: "Mon Projet" -> "mon-projet")
        if (!it) {
          const fallbackSlug = slugify(decodedSlug);
          console.log("DEBUG InfoColab: Fallback slug", fallbackSlug);

          if (fallbackSlug && fallbackSlug !== decodedSlug) {
            const filter2 = `slug = "${String(fallbackSlug).replace(/"/g, '\\"')}" && is_published = true`;
            console.log("DEBUG InfoColab: Filter 2", filter2);

            const res2 = await pb.collection('site_realisations').getList(1, 1, {
              filter: filter2,
              expand: 'category_id',
            });
            it = res2?.items?.[0] || null;
            console.log("DEBUG InfoColab: Result 2", it);
          }
        }

        if (mounted) {
          setProject(it);
          if (it) {
            // Une fois le projet chargé, on charge les "similaires"
            loadSimilarProjects(it);
          }
        }
      } catch (err) {
        console.error("Erreur chargement projet:", err);
        if (mounted) setProject(null);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchOne();
    return () => { mounted = false; };
  }, [projectSlug]);

  // Fonction pour charger les projets similaires
  const loadSimilarProjects = async (currentProject) => {
    try {
      const filterParts = [`is_published = true`, `id != "${currentProject.id}"`];

      if (currentProject.category_id) {
        filterParts.push(`category_id = "${currentProject.category_id}"`);
      }

      const res = await pb.collection('site_realisations').getList(1, 10, {
        sort: '-published_at,-created',
        filter: filterParts.join(' && '),
        expand: 'category_id'
      });
      setSimilarProjects(res.items);
    } catch (err) {
      console.error("Erreur chargement projets similaires:", err);
    }
  };

  // State pour la lightbox
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0); // Index de l'image affichée dans la galerie e-commerce

  // Créer une liste de toutes les images du projet (couverture + galerie)
  // useMemo pour éviter de recalculer à chaque rendu si project ne change pas
  const allProjectImages = useMemo(() => {
    if (!project) return [];
    const images = [];
    const cover = pbFileUrl(project, 'cover_image', '2400x0');
    if (cover) {
      images.push(cover);
    }
    if (Array.isArray(project.gallery) && project.gallery.length > 0) {
      const gal = project.gallery
        .map((f) => {
          try { return pb.files.getUrl(project, f, { thumb: '2400x0' }); } catch { return ''; }
        })
        .filter(Boolean);
      images.push(...gal);
    }
    return images;
  }, [project]);

  const openLightbox = (index) => {
    setCurrentImageIndex(index);
    setIsLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setIsLightboxOpen(false);
    setCurrentImageIndex(null);
    document.body.style.overflow = 'auto';
  };

  const goToNextImage = () => {
    setCurrentImageIndex(prevIndex =>
      prevIndex !== null && prevIndex < allProjectImages.length - 1 ? prevIndex + 1 : prevIndex
    );
  };

  const goToPrevImage = () => {
    setCurrentImageIndex(prevIndex =>
      prevIndex !== null && prevIndex > 0 ? prevIndex - 1 : prevIndex
    );
  };

  if (!loading && !project) {
    return (
      <>
        <Helmet>
          <title>Projet non trouvé - INFO COLAB</title>
          <meta name="description" content="Ce projet de INFO COLAB n'a pas été trouvé ou a été déplacé." />
        </Helmet>
        <Navigate to="/404" replace />
      </>
    );
  }

  if (loading) {
    return (
      <>
        <Helmet>
          <title>Chargement… - INFO COLAB</title>
        </Helmet>
        <div className="container project-detail-page-container">
          <p style={{ opacity: 0.8 }}>Chargement…</p>
        </div>
      </>
    );
  }

  const pageUrl = `${siteUrl}/realisations/${project.slug}`;
  const pageTitle = `${project.title} - Réalisation INFO COLAB`;
  const descBase = (project.excerpt || '') || (project.content ? String(project.content).replace(/<[^>]*>/g, '') : '');
  const metaDescription = descBase
    ? (descBase.length > 160 ? descBase.substring(0, 157) + '...' : descBase)
    : "Réalisation INFO COLAB";
  const coverForOg = pbFileUrl(project, 'cover_image', '1200x0');
  const ogImageUrl = coverForOg || `${siteUrl}/images/og-default-project.jpg`;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={pageUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content={ogImageUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="INFO COLAB" />
        {project.client && <meta property="article:author" content={project.client} />}
        {(() => {
          if (!project.date) return null;
          const d = new Date(project.date);
          if (isNaN(d.getTime())) return null;
          return <meta property="article:published_time" content={d.toISOString()} />;
        })()}
        {project.category && <meta property="article:section" content={project.category} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={ogImageUrl} />
      </Helmet>

      <div className="container project-detail-page-container">
        <Link to="/realisations" className="back-to-portfolio-link">&larr; Toutes nos réalisations</Link>

        <header className="project-header-detail">
          <h1>{project.title}</h1>
          <div className="project-meta-info">
            {project.published_at && <span><strong>Date :</strong> {new Date(project.published_at).toLocaleDateString()}</span>}
            {project.expand?.category_id?.name && <span><strong>Catégorie :</strong> {project.expand.category_id.name}</span>}
            {project.project_url && (
              <span>
                <strong>Site :</strong> <a href={project.project_url} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>{project.project_url.replace(/^https?:\/\//, '')}</a>
              </span>
            )}
          </div>
        </header>

        {/* LIVE SITE PREVIEW */}
        {project.project_url && (
          <LiveSiteViewer url={project.project_url} title={project.title} />
        )}

        <div className="project-content-full">
          <h2 className="section-subtitle">À propos du projet</h2>
          {project.content ? (
            <div dangerouslySetInnerHTML={{ __html: project.content }} />
          ) : (
            <p>{project.excerpt || ''}</p>
          )}

          {/* Note: L'ancienne grille de galerie a été supprimée car intégrée au viewer e-commerce ci-dessus */}
        </div>

        {/* SECTION TECHNOLOGIES (Séparée du contenu principal) */}
        {project.technologies && (
          <div className="container project-technologies-wrapper">
            <h3 className="technologies-title-separate">Technologies Utilisées</h3>
            <ul className="technologies-list-detail">
              {(Array.isArray(project.technologies)
                ? project.technologies
                : String(project.technologies).split(',').map(t => t.trim())
              ).filter(Boolean).map((tech, idx) => (
                <li key={idx}>{tech}</li>
              ))}
            </ul>
          </div>
        )}

        {/* GALERIE E-COMMERCE START */}
        {allProjectImages.length > 0 && (
          <div className="ecommerce-gallery-container">
            {/* Grande image principale */}
            <div className="ecommerce-main-image-wrapper" onClick={() => openLightbox(activeImageIndex)}>
              <img
                src={allProjectImages[activeImageIndex]}
                alt={`Vue principale - ${project.title}`}
                className="ecommerce-main-image"
              />
            </div>

            {/* Bandeau de miniatures */}
            {allProjectImages.length > 1 && (
              <div className="ecommerce-thumbnails-track">
                {allProjectImages.map((imgUrl, idx) => (
                  <div
                    key={idx}
                    className={`ecommerce-thumbnail ${idx === activeImageIndex ? 'active' : ''}`}
                    onClick={() => setActiveImageIndex(idx)}
                  >
                    <img src={imgUrl} alt={`Miniature ${idx + 1}`} />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {/* GALERIE E-COMMERCE END */}

        {/* SECTION PROJETS SIMILAIRES */}
        {similarProjects.length > 0 && (
          <div style={{ marginTop: '6rem', borderTop: '1px solid var(--border-color)', paddingTop: '4rem' }}>
            <h2 className="section-subtitle" style={{ textAlign: 'center', marginBottom: '2rem' }}>Ces projets pourraient aussi vous intéresser</h2>
            <Swiper
              modules={[Navigation, Pagination, A11y, Autoplay]}
              spaceBetween={32}
              slidesPerView={1}
              loop={similarProjects.length > 2}
              autoplay={{
                delay: 3000,
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
              }}
              navigation
              pagination={{ clickable: true }}
              breakpoints={{
                650: { slidesPerView: 1.2 },
                900: { slidesPerView: 2 },
                1100: { slidesPerView: 2.5 }
              }}
              className="highlight-projects-swiper"
            >
              {similarProjects.map((proj) => {
                const coverUrl = pbFileUrl(proj, 'cover_image', '800x600');
                const categoryName = proj.expand?.category_id?.name || proj.category || 'Réalisation';

                return (
                  <SwiperSlide key={proj.id} className="highlight-projects-slide">
                    <div className="highlight-project-card" style={{ width: '100%' }}>
                      <Link to={`/realisations/${encodeURIComponent(proj.slug)}`} className="project-card-link" onClick={() => window.scrollTo(0, 0)}>
                        {coverUrl ? (
                          <img src={coverUrl} alt={proj.title} className="highlight-project-image" />
                        ) : (
                          <div className="highlight-project-image placeholder"></div>
                        )}
                        <div className="highlight-project-content">
                          <h3>{proj.title}</h3>
                          <p className="project-category">{categoryName}</p>
                        </div>
                      </Link>
                    </div>
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </div>
        )}

      </div>

      {isLightboxOpen && (
        <LightboxGallery
          images={allProjectImages}
          currentIndex={currentImageIndex}
          onClose={closeLightbox}
          onNext={goToNextImage}
          onPrev={goToPrevImage}
        />
      )}
    </>
  );
}

export default ProjectDetailPage;